import { useEffect, useMemo, useState } from "react";
import { Alert, App, Button, Card, Checkbox, Col, InputNumber, Input, Modal, Popconfirm, Row, Select, Space, Spin, Switch, Tabs, Tag, Tooltip, Typography } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  EVENT_BY_KIND,
  NOTIFICATION_EVENTS,
  NOTIFICATION_GROUPS,
  countDisabledEvents,
  rolesOffered,
  type NotificationGroup,
  type NotificationKind,
  type NotificationsConfig,
  type RecipientRole,
} from "@audit-plan/shared";
import { ApiError } from "../../api/client";
import { useNotifConfig, useNotifPreview, useSaveNotifConfig, useTestNotification } from "../../api/admin";
import { Pill } from "../pills";
import { useIsMobile } from "../useIsMobile";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TZ_FALLBACK = ["Europe/Rome", "Europe/London", "Europe/Paris", "Europe/Berlin", "UTC"];
const zones = (): string[] => {
  try {
    return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? TZ_FALLBACK;
  } catch {
    return TZ_FALLBACK;
  }
};

function PreviewModal({ kind, onClose }: { kind: NotificationKind | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [lang, setLang] = useState<"it" | "en">("it");
  const q = useNotifPreview(kind, lang);
  const mobile = useIsMobile();
  return (
    <Modal open={!!kind} onCancel={onClose} footer={null} width={mobile ? "100%" : 640} title={kind ? t("n_previewTitle", { e: t(`n_ev_${kind}`) }) : ""} style={mobile ? { top: 0, maxWidth: "100vw", margin: 0 } : undefined}>
      <Tabs
        activeKey={lang}
        onChange={(k) => setLang(k as "it" | "en")}
        items={[
          { key: "it", label: "Italiano" },
          { key: "en", label: "English" },
        ]}
      />
      {q.isLoading ? (
        <Spin />
      ) : (
        <div>
          <Typography.Text strong style={{ overflowWrap: "anywhere" }}>{q.data?.subject}</Typography.Text>
          <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 13, margin: "8px 0 0", padding: 12, borderRadius: 8, background: "rgba(128,128,128,.08)", overflowWrap: "anywhere" }}>{q.data?.text}</pre>
        </div>
      )}
      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>{t("n_previewNote")}</div>
    </Modal>
  );
}

/** Amministrazione → Notifiche → Impostazioni: quando e come inviare, evento per evento. */
export function NotificationSettings() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const q = useNotifConfig();
  const save = useSaveNotifConfig();
  const test = useTestNotification();
  const mobile = useIsMobile();
  const [draft, setDraft] = useState<NotificationsConfig | null>(null);
  const [preview, setPreview] = useState<NotificationKind | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const saved = q.data?.config;
  useEffect(() => {
    if (saved && !draft) setDraft(structuredClone(saved));
  }, [saved, draft]);
  const dirty = useMemo(() => !!draft && !!saved && JSON.stringify(draft) !== JSON.stringify(saved), [draft, saved]);
  const zoneOptions = useMemo(() => zones().map((z) => ({ value: z, label: z })), []);

  if (q.isLoading || !draft || !saved) return <Spin style={{ display: "block", margin: 32 }} />;
  if (q.error) return <Alert type="error" showIcon message={q.error instanceof ApiError ? q.error.message : t("a_loadFail")} />;

  const fail = (e: unknown) => void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));
  const patch = (fn: (n: NotificationsConfig) => void) =>
    setDraft((d) => {
      const n = structuredClone(d!);
      fn(n);
      return n;
    });
  const persist = async (next: NotificationsConfig) => {
    try {
      const r = await save.mutateAsync(next);
      setDraft(structuredClone(r.config));
      void message.success(t("saved"));
    } catch (e) {
      fail(e);
    }
  };
  const setAll = (on: boolean) => {
    const next = structuredClone(draft);
    next.enabled = on;
    if (on) for (const ev of NOTIFICATION_EVENTS) next.events[ev.kind].enabled = true;
    void persist(next);
  };
  const doTest = async (kind: NotificationKind) => {
    setTesting(kind);
    try {
      const r = await test.mutateAsync(kind);
      void message.success(t("n_testQueued", { to: r.to }));
    } catch (e) {
      fail(e);
    } finally {
      setTesting(null);
    }
  };

  const off = countDisabledEvents(saved);
  const total = NOTIFICATION_EVENTS.length;
  const rem = draft.reminders;
  const headBeforeManager = rem.functionHeadAfterDays < rem.managerAfterDays;
  const numField = (label: string, value: number, onChange: (v: number) => void, max = 365, min = 1) => (
    <Col xs={24} sm={12} lg={6}>
      <div style={{ fontSize: 13, marginBottom: 4 }}>{label}</div>
      <InputNumber min={min} max={max} style={{ width: "100%" }} value={value} onChange={(v) => v != null && onChange(Math.round(v))} />
    </Col>
  );

  const eventCard = (kind: NotificationKind) => {
    const def = EVENT_BY_KIND[kind] as { locked?: boolean; critical?: boolean; defaultEnabled: boolean };
    const ec = draft.events[kind];
    const roles = rolesOffered(kind);
    const locked = !!def.locked;
    const muted = !ec.enabled;
    const sw = (
      <Switch
        checked={ec.enabled}
        disabled={locked}
        aria-label={t(`n_ev_${kind}`)}
        onChange={(v) => {
          if (def.critical && !v) return;
          patch((n) => (n.events[kind].enabled = v));
        }}
      />
    );
    return (
      <div key={kind} data-kind={kind} style={{ padding: "14px 16px", borderTop: "1px solid rgba(128,128,128,.18)" }}>
        <Row gutter={[16, 10]} align="top">
          <Col xs={24} lg={9}>
            <Space align="start" size={12} style={{ width: "100%" }}>
              {def.critical && ec.enabled ? (
                <Popconfirm title={t("n_criticalOffQ")} okText={t("n_turnOff")} cancelText={t("cancel")} onConfirm={() => patch((n) => (n.events[kind].enabled = false))}>
                  <span>{sw}</span>
                </Popconfirm>
              ) : (
                sw
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                  {t(`n_ev_${kind}`)} {locked && <Tag icon={<LockOutlined />} bordered={false}>{t("n_locked")}</Tag>}
                  {!locked && !def.defaultEnabled && !ec.enabled && <Tag bordered={false}>{t("n_offByDefault")}</Tag>}
                </div>
                <div style={{ fontSize: 12.5, opacity: 0.72 }}>{t(`n_evd_${kind}`)}</div>
                <div style={{ fontSize: 11.5, opacity: 0.5, fontFamily: "monospace", marginTop: 2 }}>{kind}</div>
                {def.critical && <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>{t("n_criticalNote")}</div>}
              </div>
            </Space>
          </Col>
          <Col xs={24} lg={15} style={{ opacity: muted ? 0.5 : 1 }}>
            {locked ? (
              <div style={{ fontSize: 12.5, opacity: 0.75 }}>{t("n_lockedNote")}</div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {roles.length > 0 && (
                  <div>
                    <div style={{ fontSize: 12.5, opacity: 0.7, marginBottom: 2 }}>{t("n_recipients")}</div>
                    <Checkbox.Group
                      disabled={muted}
                      value={ec.roles}
                      onChange={(v) => patch((n) => (n.events[kind].roles = v as RecipientRole[]))}
                      options={roles.map((r) => ({ value: r, label: t(`n_role_${r}`) }))}
                      style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px" }}
                    />
                    {!ec.roles.length && !ec.extra.length && ec.enabled && <div style={{ fontSize: 12, color: "#b26a00", marginTop: 2 }}>{t("n_noRoles")}</div>}
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 12.5, opacity: 0.7, marginBottom: 2 }}>{t("n_extra")}</div>
                  <Select
                    mode="tags"
                    disabled={muted}
                    style={{ width: "100%" }}
                    open={false}
                    tokenSeparators={[",", ";", " "]}
                    placeholder={t("n_extraPh")}
                    value={ec.extra}
                    onChange={(v: string[]) => {
                      const bad = v.find((x) => !EMAIL.test(x.trim()));
                      if (bad) void message.warning(t("n_extraBad", { v: bad }));
                      patch((n) => (n.events[kind].extra = v.map((x) => x.trim().toLowerCase()).filter((x) => EMAIL.test(x))));
                    }}
                  />
                </div>
                <Space size={16} wrap>
                  <span style={{ fontSize: 12.5, opacity: 0.7 }}>{t("n_channel")}</span>
                  <Checkbox checked disabled>{t("n_chMail")}</Checkbox>
                  <Tooltip title={t("n_soon")}>
                    <span>
                      <Checkbox checked={false} disabled>
                        {t("n_chTeams")} <Tag bordered={false} style={{ marginLeft: 4 }}>{t("n_soon")}</Tag>
                      </Checkbox>
                    </span>
                  </Tooltip>
                </Space>
              </div>
            )}
            <Space wrap style={{ marginTop: 10 }}>
              <Button size="small" onClick={() => setPreview(kind)}>{t("n_preview")}</Button>
              <Button size="small" loading={testing === kind} onClick={() => void doTest(kind)}>{t("n_testMe")}</Button>
            </Space>
          </Col>
        </Row>
      </div>
    );
  };

  const group = (g: NotificationGroup) => (
    <Card key={g} title={t(`n_grp_${g}`)} styles={{ body: { padding: 0 } }} style={{ marginBottom: 16 }}>
      {g === "reminders" && (
        <div style={{ padding: "10px 16px", fontSize: 12.5, opacity: 0.75 }}>
          {t("n_remParams")} ({t("n_leadDays")}: {draft.reminders.leadDays.join(", ")})
        </div>
      )}
      {NOTIFICATION_EVENTS.filter((e) => e.group === g).map((e) => eventCard(e.kind))}
    </Card>
  );

  return (
    <div style={{ padding: mobile ? 12 : 16 }}>
      <Card size="small" style={{ marginBottom: 16 }} data-testid="notif-state">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ minWidth: 0, flex: "1 1 280px" }}>
            <Space wrap>
              <Pill tone={saved.enabled ? (off ? "warn" : "ok") : "bad"}>{saved.enabled ? t("n_stateOn") : t("n_stateOffShort")}</Pill>
              {saved.enabled && off > 0 && <Pill tone="warn">{t("n_stateSome", { n: off })}</Pill>}
            </Space>
            <div style={{ fontSize: 13, marginTop: 6 }}>{saved.enabled ? (off ? t("n_stateSomeLong", { n: off, tot: total }) : t("n_stateAllOn")) : t("n_stateOff")}</div>
            <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
              {t("n_provider", { p: q.data!.provider })} · {q.data!.windowOpen ? t("n_queuedOpen", { n: q.data!.queued }) : t("n_queuedClosed", { n: q.data!.queued })}
            </div>
          </div>
          <Space wrap>
            <Popconfirm title={t("n_enableAllQ")} onConfirm={() => setAll(true)} okText={t("n_enableAll")} cancelText={t("cancel")}>
              <Button loading={save.isPending}>{t("n_enableAll")}</Button>
            </Popconfirm>
            <Popconfirm title={t("n_disableAllQ")} onConfirm={() => setAll(false)} okText={t("n_disableAll")} okButtonProps={{ danger: true }} cancelText={t("cancel")}>
              <Button danger loading={save.isPending}>{t("n_disableAll")}</Button>
            </Popconfirm>
          </Space>
        </div>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={14}>
          <Card title={t("n_whenTitle")} size="small" style={{ height: "100%" }}>
            <div style={{ fontSize: 13, marginBottom: 4 }}>{t("n_leadDays")}</div>
            <Select
              mode="tags"
              style={{ width: "100%" }}
              open={false}
              tokenSeparators={[",", ";", " "]}
              value={rem.leadDays.map(String)}
              onChange={(v: string[]) => {
                const nums = [...new Set(v.map((x) => Number(x)).filter((x) => Number.isInteger(x) && x >= 0 && x <= 365))].sort((a, b) => b - a);
                patch((n) => (n.reminders.leadDays = nums.length ? nums : n.reminders.leadDays));
              }}
            />
            <div style={{ fontSize: 12, opacity: 0.65, margin: "4px 0 12px" }}>{t("n_leadDaysHelp")}</div>
            <Row gutter={[12, 12]}>
              {numField(t("n_overdueEvery"), rem.overdueEveryDays, (v) => patch((n) => (n.reminders.overdueEveryDays = v)), 90)}
              {numField(t("n_managerAfter"), rem.managerAfterDays, (v) => patch((n) => (n.reminders.managerAfterDays = v)))}
              {numField(t("n_headAfter"), rem.functionHeadAfterDays, (v) => patch((n) => (n.reminders.functionHeadAfterDays = v)))}
            </Row>
            {headBeforeManager && <Alert style={{ marginTop: 10 }} type="warning" showIcon message={t("n_thresholdOrder")} />}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title={t("n_windowTitle")} size="small" style={{ height: "100%" }}>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <Space>
                <Switch checked={draft.window.hoursEnabled} onChange={(v) => patch((n) => (n.window.hoursEnabled = v))} aria-label={t("n_windowHours")} />
                <span>{t("n_windowHours")}</span>
              </Space>
              {draft.window.hoursEnabled && (
                <Space wrap>
                  <span>{t("n_windowFrom")}</span>
                  <Input type="time" style={{ width: 120 }} value={draft.window.from} onChange={(e) => e.target.value && patch((n) => (n.window.from = e.target.value))} />
                  <span>{t("n_windowTo")}</span>
                  <Input type="time" style={{ width: 120 }} value={draft.window.to} onChange={(e) => e.target.value && patch((n) => (n.window.to = e.target.value))} />
                </Space>
              )}
              <div>
                <div style={{ fontSize: 13, marginBottom: 4 }}>{t("n_windowTz")}</div>
                <Select showSearch style={{ width: "100%" }} value={draft.window.timezone} options={zoneOptions} onChange={(v) => patch((n) => (n.window.timezone = v))} />
              </div>
              <Space align="start">
                <Switch checked={draft.window.workdaysOnly} onChange={(v) => patch((n) => (n.window.workdaysOnly = v))} aria-label={t("n_windowWorkdays")} />
                <span>{t("n_windowWorkdays")}</span>
              </Space>
              <div style={{ fontSize: 12, opacity: 0.65 }}>{t("n_windowHelp")}</div>
            </Space>
          </Card>
        </Col>
      </Row>

      {NOTIFICATION_GROUPS.map(group)}

      <div style={{ position: "sticky", bottom: 0, zIndex: 5, padding: "10px 16px", margin: "0 -4px", display: dirty ? "flex" : "none", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", background: "var(--ant-color-bg-container, #fff)", borderTop: "1px solid rgba(128,128,128,.3)", boxShadow: "0 -4px 12px rgba(0,0,0,.06)" }}>
        <span style={{ fontWeight: 600 }}>{t("n_saveBar")}</span>
        <Space>
          <Button onClick={() => setDraft(structuredClone(saved))}>{t("n_discard")}</Button>
          <Button type="primary" loading={save.isPending} disabled={headBeforeManager} onClick={() => void persist(draft)}>{t("save")}</Button>
        </Space>
      </div>
      <PreviewModal kind={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
